import { styled } from 'styled-components';
import ReactSlider from 'react-slider';
import { HEADER_HEIGHT } from '../Header/Header.styles';
import { getPalette } from '../../utils/visualisations/visPalettes';
import { getRosterPositionColorByCode } from '../../utils/helpers/positions';

/* The Positions tab of the Personnel filter shows one pill per position group,
   each bordered in that position's colour so the pill agrees with the
   visualisations. Those colours are a chart palette, so they are read from
   visPalettes.js rather than named as theme tokens. See ADR-0002.

   The order matters. The TE pill carries both classes ("position ol te"), so the
   rule written later wins, and te must stay after ol to keep that pill blue. */
const PERSONNEL_POSITION_KEYS = [
  'ol',
  'te',
  'wr',
  'rb',
  'qb',
  'dl',
  'lb',
  'db',
];

/* The pill border, and the slider inside the pill's hover panel, in each
   position's own colour. */
export const positionColourRules = (theme) => {
  const palette = getPalette(theme.isDark);
  return PERSONNEL_POSITION_KEYS.map((key) => {
    const colour = getRosterPositionColorByCode(key.toUpperCase(), palette);
    return `
      &.${key} {
        border-color: ${colour};

        .drop .two-handle-slider {
          .track.track-1 {
            background: ${theme.applyOpacity(colour, 'overlay')};
            border-color: ${colour};
          }

          .thumb {
            background: ${colour};
            border-color: ${colour};
          }
        }
      }
    `;
  }).join('');
};

export const CONTEXT_FILTER_HEIGHT = '2.875rem';

export const StyledContextFilters = styled.div`
  position: fixed;
  top: ${HEADER_HEIGHT};
  left: 0;
  width: 100%;

  ${({ theme }) => theme.mqFrom('md')} {
    left: 3.5rem;
    width: calc(100% - 3.5rem);
  }

  z-index: 99;
  display: flex;
  align-items: center;
  height: ${CONTEXT_FILTER_HEIGHT};
  /* 58 + 20 */
  box-shadow: ${({ theme }) => theme.effects.dropShadow.uber};
  background-color: ${({ theme }) => theme.colours.canvas.tertiary.main};
`;
StyledContextFilters.displayName = 'StyledContextFilters';

export const FiltersLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.bodyRegular.fontSize};
  font-weight: 600;
  padding: 0 ${({ theme }) => theme.sizing.medium};
  flex-direction: row;
  display: flex;
  line-height: 2;
  color: ${({ theme }) => theme.colours.ink.primary.main};

  span {
    margin-left: 0.625rem;
  }

  svg {
    margin-top: 0.12rem;
  }
`;
FiltersLabel.displayName = 'FiltersLabel';

export const FiltersContainer = styled.div`
  flex: 1;
  display: flex;
  gap: ${({ theme }) => theme.sizing.regular};

  .ui {
    &.button {
      font-size: ${({ theme }) => theme.typography.bodyMedium.fontSize};
      color: ${({ theme }) => theme.colours.brand.weak};
      line-height: 1;
      padding: 0 ${({ theme }) => theme.sizing.regular};
      font-weight: 600;
      background-color: ${({ theme }) => theme.colours.brand.main};
      position: relative;

      &.clear {
        border: 1px ${({ theme }) => theme.colours.brand.weak} solid;
        background-color: transparent;
        color: ${({ theme }) => theme.colours.brand.weak};
        font-weight: 600;

        &.right {
          margin-left: auto;
          margin-right: ${({ theme }) => theme.sizing.large};
        }

        &:hover {
          background-color: ${({ theme }) => theme.colours.layering[0].main};
        }
      }
    }

    &.label {
      font-size: ${({ theme }) => theme.typography.bodySmall.fontSize};
      border-radius: 50%;
      padding: ${({ theme }) => theme.sizing.tiny}
        ${({ theme }) => theme.sizing.xsmall}
        ${({ theme }) => theme.sizing.tiny};
      background-color: ${({ theme }) => theme.colours.messaging.info.weak};
      color: ${({ theme }) => theme.colours.messaging.info.ink};
      line-height: 1;
      margin: ${({ theme }) => theme.sizing.xsmall} 0 0 -7px !important;
    }
  }
`;
FiltersContainer.displayName = 'FiltersContainer';

export const StyledSlider = styled(ReactSlider)`
  padding: ${({ theme }) => theme.sizing.small} 0;
  width: 100%;
  max-width: 500px;
  margin: auto;
  overflow: hidden;
  min-height: 1.2rem;

  &.grey {
    .thumb {
      background: ${({ theme }) => theme.colours.graphics.weak};
    }

    .track.track-1 {
      background: ${({ theme }) => theme.colours.graphics.weak};
      border: 1px solid ${({ theme }) => theme.colours.graphics.weak};
    }
  }

  &.inverted {
    .track.track-0 {
      z-index: 2;
    }

    .track.track-1 {
      z-index: 1;
    }

    .track.track-2 {
      z-index: 0;
    }

    .thumb {
      z-index: 3 !important;
    }
  }

  .thumb {
    background-color: ${({ theme }) => theme.colours.interface.main};
    border: ${({ theme }) => `2px solid ${theme.colours.canvas.tertiary.main}`};
    border-radius: ${({ theme }) => theme.borderRadius.ellipse};
    box-sizing: border-box;
    cursor: pointer;
    height: 16px;
    width: 16px;
    -webkit-appearance: none;
    margin-top: -7px;
  }

  .track {
    position: relative;
    cursor: default;
    height: 8px;
    width: 100%;

    &.track-0 {
      background-color: ${({ theme }) => theme.colours.canvas.primary.main};
    }

    &.track-1 {
      border: 1px solid ${({ theme }) => theme.colours.interface.main};
      background-color: ${({ theme }) => theme.colours.interface.main};
    }

    &.track-2 {
      background-color: ${({ theme }) => theme.colours.canvas.primary.main};
    }
  }
`;
StyledSlider.displayName = 'StyledSlider';

export const TriggerButtonContainer = styled.div`
  position: relative;
`;
TriggerButtonContainer.displayName = 'TriggerButtonContainer';

export const ClearFilters = styled.div`
  margin-left: auto;
  margin-right: 0.625rem;
`;
ClearFilters.displayName = 'ClearFilters';

export const SelectAllButtonContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.sizing.small};
  margin: ${({ theme }) => theme.sizing.medium} 0
    ${({ theme }) => theme.sizing.regular} 0;
  width: 18.75rem;
`;
SelectAllButtonContainer.displayName = 'SelectAllButtonContainer';

export const FilterRoundel = styled.div`
  font-size: ${({ theme }) => theme.typography.bodySmall.fontSize};
  border-radius: 50%;
  padding: ${({ theme }) => theme.sizing.tiny}
    ${({ theme }) => theme.sizing.xsmall} ${({ theme }) => theme.sizing.tiny};
  background-color: ${({ theme }) => theme.colours.messaging.info.main};
  color: ${({ theme }) => theme.colours.messaging.info.ink};
  line-height: 1;
  margin: ${({ theme }) => theme.sizing.xsmall} 0 0 -11px !important;
  position: absolute;
  z-index: 100;
  top: -1em;
  left: 100%;
  font-weight: bold;
  opacity: ${({ $opacity }) => $opacity || 1};
`;

export const FiltersHR = styled.hr`
  margin: ${({ $margin }) => $margin || 0};
  border-color: ${({ theme }) => theme.colours.canvas.secondary.main};
`;

export const FiltersShortcuts = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.sizing.small};
  padding-top: ${({ theme }) => theme.sizing.small};
`;

export const ModalWidth = styled.div`
  min-width: ${({ $minWidth }) => $minWidth || 'auto'};
`;

export const FilterDynamicValue = styled.b`
  position: absolute;
  top: ${({ theme }) => theme.sizing.regular};
  right: 0;
  font-size: ${({ theme }) => theme.typography.bodyMedium.fontSize};
  padding-left: ${({ theme }) => theme.sizing.tiny};
  color: ${({ theme }) => theme.colours.ink.primary.main} !important;

  &.grey {
    color: ${({ theme }) =>
      theme.applyOpacity(
        theme.colours.ink.primary.main,
        'disabled',
      )} !important;
  }
`;

export const FiltersTrack = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  font-size: ${({ theme }) => theme.typography.bodySmall.fontSize};
  color: ${({ theme }) => theme.colours.ink.primary.main};
  opacity: ${({ $opacity }) => $opacity || 1};

  &.labels {
    padding: 0;
  }

  &.extreme {
    position: absolute;
    top: 9px;
    font-size: ${({ theme }) => theme.typography.headingRegular.fontSize};
  }
`;

export const FormationsLayout = styled.div`
  .field,
  .fields {
    display: flex;
    width: max-content;

    &.no-margin {
      margin: 0;
    }
  }

  .half {
    width: 100%;
    display: inline-block;
  }

  @media (min-width: 1024px) {
    .half {
      width: 35%;
      display: inline-block;
    }

    .half + .half {
      width: 65%;
    }
  }

  .packages {
    display: flex;
    flex-direction: column;
    flex: auto;
    grid-gap: 12px;
    margin: ${({ theme }) => theme.sizing.tiny};
    padding-right: ${({ theme }) => theme.sizing.medium};

    &.defence {
      flex-direction: row;
      grid-gap: 8px;
      margin: ${({ theme }) => theme.sizing.tiny};
      display: grid;
      grid-template-columns: 50% 50%;
      grid-template-rows: 50% 50%;
    }
  }

  h3 {
    margin-bottom: ${({ theme }) => theme.sizing.small};
    font-size: ${({ theme }) => theme.typography.bodyMedium.fontSize};
  }

  .packages + .packages {
    margin-left: auto;
  }
`;

export const PersonnelLayout = styled.div`
  margin: ${({ theme }) => theme.sizing.small} 0
    ${({ theme }) => theme.sizing.xlarge} 0;

  .field,
  .fields {
    display: flex;
    width: max-content;

    &.no-margin {
      margin: 0;
    }
  }

  .half {
    width: 100%;
    display: inline-block;
  }

  @media (min-width: 1024px) {
    .half {
      width: 35%;
      display: inline-block;
    }

    .half + .half {
      width: 65%;
    }
  }

  .packages {
    display: flex;
    flex-direction: column;
    flex: auto;

    &.defence {
      flex-direction: row;
      grid-gap: 0.25rem;
      display: grid;
      grid-template-columns: 50% 50%;
      grid-template-rows: 50% 50%;
    }
  }

  h3 {
    margin-bottom: ${({ theme }) => theme.sizing.small};
    font-size: ${({ theme }) => theme.typography.bodyMedium.fontSize};
  }

  .packages + .packages {
    margin-left: auto;
  }

  .info {
    &.isDisabled {
      .tiles {
        pointer-events: none;
      }
    }

    h3:first-child {
      text-align: center;
      margin: 0 0 ${({ theme }) => theme.sizing.small};
    }

    h3 {
      text-align: center;
      margin: ${({ theme }) => theme.sizing.small} 0
        ${({ theme }) => theme.sizing.small};
      opacity: ${({ $opacity }) => $opacity || 1};
    }

    .tiles {
      display: flex;
      grid-gap: ${({ theme }) => theme.sizing.regular};
      margin-bottom: ${({ theme }) => theme.sizing.regular};
      align-items: center;
      justify-content: center;
    }

    .track {
      width: 100%;
      display: flex;
      justify-content: space-between;
      font-size: ${({ theme }) => theme.typography.bodySmall.fontSize};
      color: ${({ theme }) => theme.colours.ink.primary.main};

      &.labels {
        margin-top: -10px;
        padding-bottom: ${({ theme }) => theme.sizing.regular};
      }

      &.extreme {
        position: absolute;
        top: ${({ theme }) => theme.sizing.medium};
        width: 99%;
        font-size: ${({ theme }) =>
          theme.typography.headingLarge.fontSize} !important;
        line-height: 1.4;
      }

      &.field,
      &.clock,
      &.olLine {
        div {
          width: ${({ theme }) => theme.sizing.medium};
        }
      }

      &.yards {
        width: calc(100% - 25px);
        margin-left: ${({ theme }) => theme.sizing.regular};

        // only has a right sided extreme
        &.right {
          margin-left: 0;

          div {
            text-align: center;
          }
        }

        div {
          width: 20px;
          text-align: right;
        }

        div.minus {
          text-align: center;
        }
      }

      &.timeToThrow {
        width: calc(100% - ${({ theme }) => theme.sizing.regular});
      }

      &.clock {
        div:last-child {
          margin-right: ${({ theme }) => theme.sizing.medium};
        }
      }

      &.clock + div {
        margin-top: ${({ theme }) => theme.sizing.small};
      }

      &.distance {
        text-align: center;
        width: calc(100% - 20px);
        margin-bottom: 0.625rem;

        div {
          width: 20px;
        }
      }
    }

    .position {
      position: relative;
      background-color: ${({ theme }) => theme.colours.canvas.secondary.main};
      border-top: 2px solid;
      padding: ${({ theme }) => theme.sizing.tiny}
        ${({ theme }) => theme.sizing.regular}
        ${({ theme }) => theme.sizing.small};
      text-align: center;
      border-radius: ${({ theme }) => theme.borderRadius.secondary};
      width: 85px;
      height: 55px;
      box-shadow: ${({ theme }) => theme.effects.dropShadow.regular};
      opacity: ${({ $opacity }) => $opacity || 1};

      .drop {
        box-shadow: ${({ theme }) => theme.effects.dropShadow.regular};
      }

      svg {
        float: right;
        top: ${({ theme }) => theme.sizing.small};
        position: relative;
        left: -8px;
      }

      &.ol {
        width: 180px;

        svg {
          left: -37%;
        }
      }

      &.te {
        width: 85px;

        svg {
          left: -12px;
        }
      }

      p {
        font-size: ${({ theme }) => theme.typography.headingRegular.fontSize};
        text-indent: -3px;
        line-height: 1.4;
      }

      label {
        letter-spacing: 1px;
        font-size: ${({ theme }) => theme.typography.bodyRegular.fontSize};
        color: ${({ theme }) => theme.colours.ink.primary.main};
      }

      &.ol {
        .drop {
          left: 0;
          border-radius: ${({ theme }) =>
            `0 0 ${theme.borderRadius.secondary} ${theme.borderRadius.secondary}`};
        }
      }

      &.te {
        .drop {
          left: -57%;
          border-radius: ${({ theme }) => theme.borderRadius.secondary};
        }
      }

      ${({ theme }) => positionColourRules(theme)}

      .drop {
        position: absolute;
        top: 48px;
        left: -57%;
        z-index: 4;
        height: 50px;
        width: 180px;
        border-radius: ${({ theme }) => theme.borderRadius.secondary};
        padding: ${({ theme }) => theme.sizing.regular}
          ${({ theme }) => theme.sizing.small} 0
          ${({ theme }) => theme.sizing.small};
        visibility: hidden;
        background-color: ${({ theme }) => theme.colours.canvas.secondary.main};

        .track {
          height: 6px;
          top: ${({ theme }) => theme.sizing.tiny};

          &.olLine {
            top: -7px;
            position: relative;
            font-size: ${({ theme }) => theme.typography.bodyRegular.fontSize};
            width: 165px;
          }
        }

        .two-handle-slider {
          width: 165px;

          .thumb {
            width: ${({ theme }) => theme.sizing.medium};
            height: ${({ theme }) => theme.sizing.medium};
            border-width: 1px;
          }
        }
      }

      &:hover {
        border-radius: ${({ theme }) =>
          `${theme.borderRadius.secondary} ${theme.borderRadius.secondary} 0 0`};
        opacity: 1;

        .drop {
          visibility: visible;
        }
      }
    }
  }
`;
